<script>
	import { findAddress, retrieveAddress } from "./addressHelpers";

	export let label = "Address";
	export let disabled = false;
	export let required = false;
	export let index = 0;
	export let address = {};
	export let showManualEntry = false;

	let addressInput = null;
	let suggestions = [];
	let highlightedIndex = -1;
	let suggestionsListElement;

	// Automatically scroll the list to keep the highlighted item in view
	$: if (suggestionsListElement && highlightedIndex > -1) {
		const highlightedElement = suggestionsListElement.children[highlightedIndex];
		if (highlightedElement) {
			highlightedElement.scrollIntoView({
				block: "nearest"
			});
		}
	}

	// Configure the address lookup.
	let debounceTimeout;
	const onInput = (event) => {
		address.AddressValid = false;
		address.ThirdPartyValidated = false;
		address.ThirdPartyValidationId = null;
		address.ValidationVendor = null;

		const query = event.target.value;
		highlightedIndex = -1; // Reset highlight on new input

		if (!query || query.length < 4) {
			suggestions = [];
			return;
		}

		clearTimeout(debounceTimeout);
		debounceTimeout = setTimeout(async () => {
			showManualEntry = true;
			suggestions = await findAddress(address.CountryCode, query);
		}, 200);
	};

	const onAddressSelect = async (selectedSuggestion) => {
		suggestions = []; // Hide the suggestions list
		addressInput.focus();

		address = await retrieveAddress(address, selectedSuggestion.id, "Loquate");

		if (!address) {
			return;
		}

		address.AddressValid = true;

		// Trigger change for validation
		addressInput.dispatchEvent(new Event("change"));
	};

	const onKeyDown = (event) => {
		if (suggestions.length === 0) return;

		switch (event.key) {
			case "ArrowDown":
				event.preventDefault();
				highlightedIndex = (highlightedIndex + 1) % suggestions.length;
				break;
			case "ArrowUp":
				event.preventDefault();
				// The addition of suggestions.length handles the negative case of -1
				highlightedIndex = (highlightedIndex - 1 + suggestions.length) % suggestions.length;
				break;
			case "Enter":
				event.preventDefault();
				if (highlightedIndex > -1) {
					onAddressSelect(suggestions[highlightedIndex]);
				}
				break;
			case "Escape":
				suggestions = [];
				break;
		}
	};

	const handleFocusOut = (event) => {
		if (!event.currentTarget.contains(event.relatedTarget)) {
			suggestions = [];
		}
	};
</script>

<div class="autocomplete-container" on:focusout={handleFocusOut}>
	<label for={`address${index}`}>
		{label}
		{#if required}
			<span class="required">*</span>
		{/if}
	</label>
	<input
		id={`address${index}`}
		{disabled}
		{required}
		type="text"
		placeholder="Type your address here"
		class="form-control address"
		bind:this={addressInput}
		bind:value={address.FormattedAddress}
		on:input={onInput}
		on:keydown={onKeyDown}
		autocomplete="off"
		autocompleteaddress
		data-address-select={address.AddressValid || address.ManualEntry} />

	{#if suggestions.length > 0}
		<ul class="suggestions-list" bind:this={suggestionsListElement}>
			{#each suggestions as suggestion, i (suggestion.fullAddress)}
				<!-- svelte-ignore a11y-click-events-have-key-events -->
				<li
					tabindex="-1"
					class:highlighted={i === highlightedIndex}
					on:mouseenter={() => (highlightedIndex = i)}
					on:click={() => onAddressSelect(suggestion)}>
					{suggestion.fullAddress}
				</li>
			{/each}
		</ul>
	{/if}
</div>
<div id="error"></div>

<style>
	.autocomplete-container {
		position: relative;
	}

	.suggestions-list {
		position: absolute;
		top: 100%;
		left: 0;
		right: 0;
		background: white;
		border: 1px solid #ccc;
		border-top: none;
		list-style-type: none;
		margin: 0;
		padding: 0;
		max-height: 200px;
		overflow-y: auto;
		z-index: 1000;
	}

	.suggestions-list li {
		padding: 8px 12px;
		cursor: pointer;
	}

	.suggestions-list li:hover,
	.suggestions-list li.highlighted {
		background-color: #f0f0f0;
	}
</style>
